import { AlertTriangle, ChevronDown, ChevronRight, Download } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { SummaryCard } from "./SummaryCard";
import { getCategoryAccentClasses, getCategoryIcon } from "./categoryIcons";
import {
  DEFICIENCY_SEVERITY_META,
  getDeficiencyCategory,
  getDeficiencyTitle,
  normalizeDeficiencySeverity,
  type DeficiencySeverity,
  type RawDeficiency,
} from "./deficiencyHelpers";

interface DeficienciesCardProps {
  defList: RawDeficiency[];
  pendingDefs: RawDeficiency[];
  topDeficiencies: RawDeficiency[];
  deficiencySeverityCounts: Record<DeficiencySeverity, number>;
  deficiencyCategoryCounts: Map<string, number>;
  recommendedStepSectionCount: number;
  actionBtnCls: string;
  onExportCSV: () => void;
  onExportXLSX: () => void;
  onOpenDeficiency: (deficiencyId?: string | null) => void;
  score?: number | null;
  recommendationTotal?: number | null;
}

export function DeficienciesCard({
  defList,
  pendingDefs,
  topDeficiencies,
  deficiencySeverityCounts,
  deficiencyCategoryCounts,
  recommendedStepSectionCount,
  actionBtnCls,
  onExportCSV,
  onExportXLSX,
  onOpenDeficiency,
  recommendationTotal,
}: DeficienciesCardProps) {
  return (
    <SummaryCard
      icon={AlertTriangle}
      iconColor={pendingDefs.length > 0 ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}
      tone={pendingDefs.length > 0 ? "danger" : "success"}
      title={
        <div>
          <div>Deficiencies</div>
          {defList.length > 0 && (
            <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
              {pendingDefs.length} identified · {recommendedStepSectionCount} recommendation groups
            </div>
          )}
        </div>
      }
      action={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={actionBtnCls} disabled={defList.length === 0}>
              <Download className="h-3 w-3" />
              Export
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExportCSV}>
              Export CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportXLSX}>
              Export XLSX
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {topDeficiencies.length > 0 ? (
        <div className="space-y-4">
          {typeof recommendationTotal === "number" && (
            <div className="flex items-center gap-6">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-foreground">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  }).format(recommendationTotal)}
                </span>
                <span className="text-xs text-muted-foreground">est. recommendations</span>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">By Type</p>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(DEFICIENCY_SEVERITY_META) as DeficiencySeverity[]).map((severity) => {
                const meta = DEFICIENCY_SEVERITY_META[severity];
                return (
                  <span
                    key={severity}
                    className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ring-1", meta.className)}
                  >
                    <span className="font-semibold">{deficiencySeverityCounts[severity]}</span>
                    {meta.label}
                  </span>
                );
              })}
            </div>
          </div>

          {deficiencyCategoryCounts.size > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">By Category</p>
              <div className="flex flex-wrap gap-2">
                {Array.from(deficiencyCategoryCounts.entries())
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, count]) => {
                    const Icon = getCategoryIcon(category);
                    return (
                      <span
                        key={category}
                        className="inline-flex items-center gap-1.5 rounded-full bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground ring-1 ring-border/40"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {category}
                        <span className="font-semibold text-foreground">{count}</span>
                      </span>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="space-y-2">
          {topDeficiencies.map((d) => {
            const severity = normalizeDeficiencySeverity(d.severity);
            const meta = DEFICIENCY_SEVERITY_META[severity];
            const category = getDeficiencyCategory(d);
            const Icon = getCategoryIcon(category);
            return (
              <button
                key={d.id ?? `${getDeficiencyTitle(d)}-${category}`}
                type="button"
                onClick={() => onOpenDeficiency(d.id)}
                className={cn(
                  "group flex w-full items-center gap-3 rounded-lg bg-muted/35 px-3.5 py-3 text-left ring-1 ring-border/20 transition-colors hover:bg-muted/50",
                  severity === "critical" && "border-l-2 border-red-500",
                  severity === "high" && "border-l-2 border-orange-500",
                  severity === "medium" && "border-l-2 border-amber-500",
                  severity === "low" && "border-l-2 border-slate-500"
                )}
              >
                <div className={cn("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border", getCategoryAccentClasses(category))}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{getDeficiencyTitle(d)}</p>
                  <p className="truncate text-xs text-muted-foreground">{category} · {d.detail ?? d.recommendation_summary ?? "No detail"}</p>
                </div>
                <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold ring-1", meta.className)}>
                  {meta.label}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground/60 transition-colors group-hover:text-foreground" />
              </button>
            );
          })}
          </div>

          {pendingDefs.length > topDeficiencies.length && (
            <button
              type="button"
              onClick={() => onOpenDeficiency()}
              className="w-full rounded-lg border border-border/30 bg-background/20 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
            >
              View all {pendingDefs.length} deficiencies →
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground/60 italic">No deficiencies</p>
      )}
    </SummaryCard>
  );
}
